import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, Mail, ExternalLink, Sparkles, Check, Euro, Copy, Link2, Ticket, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useNoIndex } from "@/lib/useNoIndex";
import { t } from "@/lib/i18n";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fi-FI", {
      day: "numeric", month: "numeric", year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function OwnerPage() {
  useNoIndex();
  const { secret } = useParams();
  const [data, setData] = useState(null);
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [events, setEvents] = useState([]);
  const [config, setConfig] = useState(null);
  const [priceConsumer, setPriceConsumer] = useState("");
  const [priceCompany, setPriceCompany] = useState("");
  const [savingPrice, setSavingPrice] = useState(false);
  const [freeLinks, setFreeLinks] = useState([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkCount, setLinkCount] = useState(1);
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [selColls, setSelColls] = useState([]);
  const [selLinks, setSelLinks] = useState([]);
  const [openAnswers, setOpenAnswers] = useState({});
  const [answersByColl, setAnswersByColl] = useState({});
  const [openNameForms, setOpenNameForms] = useState({});
  const [nameFormDrafts, setNameFormDrafts] = useState({});

  const origin = window.location.origin;

  useEffect(() => {
    Promise.all([
      api.get(`/owner/${secret}`),
      api.get(`/owner/${secret}/occasions`),
      api.get(`/owner/${secret}/events`),
      api.get(`/owner/${secret}/config`),
      api.get(`/owner/${secret}/free-links`),
    ])
      .then(([d, o, e, c, f]) => {
        setData(d.data);
        setOccasions(o.data);
        setEvents(e.data);
        setConfig(c.data);
        setPriceConsumer(String(c.data.price_consumer));
        setPriceCompany(String(c.data.price_company));
        setFreeLinks(f.data);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [secret]);

  const refreshLinks = async () => {
    const { data } = await api.get(`/owner/${secret}/free-links`);
    setFreeLinks(data);
  };

  const refreshDashboard = async () => {
    const { data } = await api.get(`/owner/${secret}`);
    setData(data);
  };

  const toggleSel = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const deleteCollections = async (ids) => {
    try {
      await api.post(`/owner/${secret}/collections/delete`, { ids });
      setSelColls((s) => s.filter((x) => !ids.includes(x)));
      await refreshDashboard();
      const [e] = await Promise.all([api.get(`/owner/${secret}/events`)]);
      setEvents(e.data);
      toast.success(t("owner.collections.deleted"));
    } catch {
      toast.error(t("common.deleteFailed"));
    }
  };

  const toggleTest = async (c, value) => {
    try {
      await api.patch(`/owner/${secret}/collections/${c.id}/test`, { is_test: value });
      await refreshDashboard();
      const e = await api.get(`/owner/${secret}/events`);
      setEvents(e.data);
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const loadAnswers = async (c) => {
    const open = !openAnswers[c.id];
    setOpenAnswers((o) => ({ ...o, [c.id]: open }));
    if (open && !answersByColl[c.id]) {
      const { data } = await api.get(`/owner/${secret}/collections/${c.id}/answers`);
      setAnswersByColl((a) => ({ ...a, [c.id]: data }));
    }
  };

  const toggleNameForms = (c) => {
    const open = !openNameForms[c.id];
    setOpenNameForms((o) => ({ ...o, [c.id]: open }));
    if (open && !nameFormDrafts[c.id]) {
      const nf = c.name_forms || {};
      setNameFormDrafts((d) => ({
        ...d,
        [c.id]: {
          fiGenitive: nf?.fi?.genitive || "",
          fiAllative: nf?.fi?.allative || "",
          deGenitive: nf?.de?.genitive || "",
          hasDe: !!nf?.de,
        },
      }));
    }
  };

  const saveNameForms = async (c) => {
    const d = nameFormDrafts[c.id] || {};
    const name_forms = {
      fi: {
        nominative: c.person_name,
        genitive: d.fiGenitive?.trim() || null,
        allative: d.fiAllative?.trim() || null,
      },
    };
    if (d.hasDe || d.deGenitive?.trim()) {
      name_forms.de = { nominative: c.person_name, genitive: d.deGenitive?.trim() || null };
    }
    try {
      await api.patch(`/owner/${secret}/collections/${c.id}/name-forms`, { name_forms });
      await refreshDashboard();
      toast.success(t("owner.nameForms.saved"));
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const deleteAnswer = async (c, answerId) => {
    try {
      await api.delete(`/owner/${secret}/collections/${c.id}/answers/${answerId}`);
      setAnswersByColl((a) => ({ ...a, [c.id]: (a[c.id] || []).filter((x) => x.id !== answerId) }));
      await refreshDashboard();
      toast.success(t("owner.collections.answerDeleted"));
    } catch {
      toast.error(t("common.deleteFailed"));
    }
  };

  const setLinkSent = async (link, value) => {
    try {
      const { data } = await api.patch(`/owner/${secret}/free-links/${link.id}`, { sent: value });
      setFreeLinks(data);
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const setLinkNote = async (link, note) => {
    try {
      const { data } = await api.patch(`/owner/${secret}/free-links/${link.id}`, { note });
      setFreeLinks(data);
    } catch {
      toast.error(t("common.saveFailed"));
    }
  };

  const deleteLinks = async (ids) => {
    try {
      await api.post(`/owner/${secret}/free-links/delete`, { ids });
      setSelLinks((s) => s.filter((x) => !ids.includes(x)));
      await refreshLinks();
      toast.success(t("owner.freeLinks.deleted"));
    } catch {
      toast.error(t("common.deleteFailed"));
    }
  };

  const createLinks = async (count) => {
    setGeneratingLinks(true);
    try {
      await api.post(`/owner/${secret}/free-links`, { count, label: linkLabel.trim() });
      await refreshLinks();
      toast.success(count === 2 ? t("owner.freeLinks.pairCreated") : t(count > 1 ? "owner.freeLinks.createdPlural" : "owner.freeLinks.createdSingular", { n: count }));
      setLinkLabel("");
      setLinkCount(1);
    } catch {
      toast.error(t("owner.freeLinks.createFailed"));
    } finally {
      setGeneratingLinks(false);
    }
  };

  const freeUrl = (token) => `${origin}/free/${token}`;

  const copyLink = (token) => {
    navigator.clipboard.writeText(freeUrl(token));
    toast.success(t("common.linkCopied"));
  };

  const copyAllUnused = () => {
    const unused = freeLinks.filter((l) => l.status === "unused" && !l.sent).map((l) => freeUrl(l.token));
    if (unused.length === 0) { toast.info(t("owner.freeLinks.noneUnused")); return; }
    navigator.clipboard.writeText(unused.join("\n"));
    toast.success(t("owner.freeLinks.copiedCount", { n: unused.length }));
  };

  const priceInvalid = (v) => {
    const n = Number(v);
    return !Number.isFinite(n) || n <= 0 || n > 100000 || String(v).trim() === "";
  };

  const savePrices = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/owner/${secret}/config`, {
        price_consumer: Number(priceConsumer),
        price_company: Number(priceCompany),
      });
      setConfig(data);
      setPriceConsumer(String(data.price_consumer));
      setPriceCompany(String(data.price_company));
      toast.success(t("owner.pricing.saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.saveFailed"));
    } finally {
      setSavingPrice(false);
    }
  };

  const setDraft = (id, field, value) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const draftVal = (o, field) => (drafts[o.id]?.[field] ?? o[field]);

  const occQ = (o, lang) => {
    const key = lang === "de" ? "qde" : "qfi";
    const fallback = (o.questions_by_lang && o.questions_by_lang[lang]) || (lang === "fi" ? o.questions : []) || [];
    return drafts[o.id]?.[key] ?? fallback;
  };
  const setOccQ = (o, lang, arr) => setDraft(o.id, lang === "de" ? "qde" : "qfi", arr);
  const updateOccQ = (o, lang, i, val) => setOccQ(o, lang, occQ(o, lang).map((q, idx) => (idx === i ? val : q)));
  const addOccQ = (o, lang) => { const arr = occQ(o, lang); if (arr.length < 10) setOccQ(o, lang, [...arr, ""]); };
  const removeOccQ = (o, lang, i) => setOccQ(o, lang, occQ(o, lang).filter((_, idx) => idx !== i));

  const occDirty = (o) => {
    const d = drafts[o.id];
    if (!d) return false;
    const curFi = (o.questions_by_lang && o.questions_by_lang.fi) || o.questions || [];
    const curDe = (o.questions_by_lang && o.questions_by_lang.de) || [];
    return (
      draftVal(o, "title") !== o.title ||
      draftVal(o, "description") !== o.description ||
      JSON.stringify(occQ(o, "fi")) !== JSON.stringify(curFi) ||
      JSON.stringify(occQ(o, "de")) !== JSON.stringify(curDe)
    );
  };

  // Plain function returning JSX (NOT a component) so textareas keep focus while typing.
  const renderOccQuestions = (o, lang) => (
    <div className="mt-4" data-testid={`occasion-questions-${lang}-${o.id}`}>
      <Label className="text-[#2C221E] text-sm">
        {lang === "de" ? t("owner.occasions.questionsDe") : t("owner.occasions.questionsFi")}
      </Label>
      {lang === "de" && (
        <p className="text-xs text-[#7A6B63] mt-1">{t("owner.occasions.deHint")}</p>
      )}
      <div className="mt-2 space-y-2">
        {occQ(o, lang).map((q, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`occasion-q-${lang}-${o.id}-${i}`}>
            <span className="mt-2 text-sm font-semibold text-[#C86D51] w-5 shrink-0">{i + 1}.</span>
            <Textarea
              value={q}
              onChange={(e) => updateOccQ(o, lang, i, e.target.value)}
              placeholder={t("owner.occasions.questionPlaceholder")}
              className="min-h-[52px] rounded-xl border-[#E8DEC2] bg-[#FDFBF7] text-sm resize-y flex-1"
              data-testid={`occasion-q-input-${lang}-${o.id}-${i}`}
            />
            <button
              type="button"
              onClick={() => removeOccQ(o, lang, i)}
              className="mt-1 p-1.5 rounded-lg text-[#B84A39] hover:bg-[#FBECE7]"
              data-testid={`occasion-q-remove-${lang}-${o.id}-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={() => addOccQ(o, lang)}
        disabled={occQ(o, lang).length >= 10}
        className="mt-2 h-9 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm"
        data-testid={`occasion-q-add-${lang}-${o.id}`}
      >
        <Plus className="w-3.5 h-3.5 mr-2" /> {t("owner.occasions.addQuestion")}
      </Button>
    </div>
  );

  const togglePublic = async (o, value) => {
    try {
      const { data } = await api.patch(`/owner/${secret}/occasions/${o.id}`, { is_public: value });
      setOccasions((occs) => occs.map((x) => (x.id === o.id ? data : x)));
      toast.success(value ? t("owner.occasions.published") : t("owner.occasions.hidden"));
    } catch {
      toast.error(t("owner.occasions.updateFailed"));
    }
  };

  const saveOccasion = async (o) => {
    try {
      const { data } = await api.patch(`/owner/${secret}/occasions/${o.id}`, {
        title: draftVal(o, "title"),
        description: draftVal(o, "description"),
        questions_by_lang: {
          fi: occQ(o, "fi"),
          de: occQ(o, "de"),
        },
      });
      setOccasions((occs) => occs.map((x) => (x.id === o.id ? data : x)));
      setDrafts((d) => { const n = { ...d }; delete n[o.id]; return n; });
      toast.success(t("owner.occasions.saved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.saveFailed"));
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[#7A6B63]">{t("common.loading")}</div>;

  if (notFound) {
    return (
      <div className="min-h-screen grid place-items-center px-4 text-center">
        <div>
          <h1 className="font-serif text-4xl text-[#2C221E]">404</h1>
          <p className="mt-2 text-[#7A6B63]">{t("owner.notFound")}</p>
        </div>
      </div>
    );
  }

  const { collections, leads } = data;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-4xl mx-auto px-4 py-10 sm:py-14">
        <div className="text-sm font-semibold tracking-wide uppercase text-[#C86D51] mb-2">Portwrite</div>
        <h1 className="font-serif text-3xl sm:text-4xl font-medium text-[#2C221E]">{t("owner.title")}</h1>

        {/* Pricing config */}
        {config && (
          <div className="mt-8 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm" data-testid="pricing-config">
            <h2 className="font-serif text-2xl text-[#2C221E] mb-1 flex items-center gap-2">
              <Euro className="w-5 h-5 text-[#C86D51]" /> {t("owner.pricing.title")}
            </h2>
            <p className="text-sm text-[#7A6B63] mb-4">
              {t("owner.pricing.summaryPre")} <strong>{config.price_consumer} €</strong> {t("owner.pricing.summaryMid")} <strong>{config.price_company} €</strong> {t("owner.pricing.summaryPost")}
              {config.price_updated_at
                ? t("owner.pricing.updatedAt", { date: new Date(config.price_updated_at).toLocaleString("fi-FI", { timeZone: "Europe/Helsinki" }) })
                : t("owner.pricing.notChanged")}
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price-consumer" className="text-[#2C221E] text-sm">{t("owner.pricing.consumerLabel")}</Label>
                <Input
                  id="price-consumer" type="number" min="1" value={priceConsumer}
                  onChange={(e) => setPriceConsumer(e.target.value)}
                  className={`h-11 rounded-xl bg-[#FDFBF7] mt-1 ${priceInvalid(priceConsumer) ? "border-[#B84A39]" : "border-[#E8DEC2]"}`}
                  data-testid="price-consumer-input"
                />
              </div>
              <div>
                <Label htmlFor="price-company" className="text-[#2C221E] text-sm">{t("owner.pricing.companyLabel")}</Label>
                <Input
                  id="price-company" type="number" min="1" value={priceCompany}
                  onChange={(e) => setPriceCompany(e.target.value)}
                  className={`h-11 rounded-xl bg-[#FDFBF7] mt-1 ${priceInvalid(priceCompany) ? "border-[#B84A39]" : "border-[#E8DEC2]"}`}
                  data-testid="price-company-input"
                />
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  disabled={savingPrice || priceInvalid(priceConsumer) || priceInvalid(priceCompany)}
                  className="mt-4 h-11 px-5 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                  data-testid="save-prices-button"
                >
                  <Check className="w-4 h-4 mr-2" /> {t("owner.pricing.save")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-2xl">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("owner.pricing.confirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("owner.pricing.confirmDesc", { consumer: priceConsumer, company: priceCompany })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={savePrices} className="bg-[#C86D51] hover:bg-[#B35A3E]" data-testid="confirm-save-prices">
                    {t("common.save")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Free links */}
        <div className="mt-8 rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm" data-testid="free-links-section">
          <h2 className="font-serif text-2xl text-[#2C221E] mb-1 flex items-center gap-2">
            <Ticket className="w-5 h-5 text-[#C86D51]" /> {t("owner.freeLinks.title")}
          </h2>
          <p className="text-sm text-[#7A6B63] mb-4">
            {t("owner.freeLinks.desc")}
          </p>
          <div className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
            <div>
              <Label htmlFor="link-label" className="text-[#2C221E] text-sm">{t("owner.freeLinks.labelLabel")}</Label>
              <Input
                id="link-label" value={linkLabel} onChange={(e) => setLinkLabel(e.target.value)}
                placeholder={t("owner.freeLinks.labelPlaceholder")}
                className="h-11 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] mt-1" data-testid="link-label-input"
              />
            </div>
            <div>
              <Label htmlFor="link-count" className="text-[#2C221E] text-sm">{t("owner.freeLinks.countLabel")}</Label>
              <Input
                id="link-count" type="number" min="1" max="100" value={linkCount}
                onChange={(e) => setLinkCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                className="h-11 w-20 rounded-xl border-[#E8DEC2] bg-[#FDFBF7] mt-1" data-testid="link-count-input"
              />
            </div>
            <Button
              onClick={() => createLinks(linkCount)} disabled={generatingLinks}
              className="h-11 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl" data-testid="create-links-button"
            >
              <Sparkles className="w-4 h-4 mr-2" /> {t("owner.freeLinks.create")}
            </Button>
            <Button
              onClick={() => createLinks(2)} disabled={generatingLinks} variant="outline"
              className="h-11 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6]" data-testid="create-pair-button"
            >
              {t("owner.freeLinks.createPair")}
            </Button>
          </div>

          {freeLinks.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <span className="text-sm text-[#7A6B63]">{t("owner.freeLinks.countBadge", { n: freeLinks.length })}</span>
                <div className="flex items-center gap-2">
                  {selLinks.length > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" className="h-9 rounded-xl border-[#B84A39] text-[#B84A39] hover:bg-[#FBECE7] text-sm" data-testid="delete-selected-links-button">
                          <Trash2 className="w-3.5 h-3.5 mr-2" /> {t("owner.freeLinks.deleteSelected")} ({selLinks.length})
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-2xl">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("owner.freeLinks.deleteTitle")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {freeLinks.some((l) => selLinks.includes(l.id) && l.status === "used")
                              ? t("owner.freeLinks.deleteUsedWarn") : t("owner.freeLinks.deleteDesc")}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteLinks(selLinks)} className="bg-[#B84A39] hover:bg-[#9d3e30]" data-testid="confirm-delete-selected-links">
                            {t("owner.freeLinks.delete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button
                    onClick={copyAllUnused} variant="outline"
                    className="h-9 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm"
                    data-testid="copy-all-unused-button"
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" /> {t("owner.freeLinks.copyAll")}
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-[#F0E8DA] border border-[#E8DEC2] rounded-xl overflow-hidden">
                {freeLinks.map((l) => {
                  const badge = {
                    unused: { t: t("owner.freeLinks.statusUnused"), c: "bg-[#EAF1EA] text-[#3f6047]" },
                    used: { t: t("owner.freeLinks.statusUsed"), c: "bg-[#F0E8DA] text-[#7A6B63]" },
                    expired: { t: t("owner.freeLinks.statusExpired"), c: "bg-[#F8E3DE] text-[#B35A3E]" },
                  }[l.status] || { t: l.status, c: "bg-[#F0E8DA] text-[#7A6B63]" };
                  return (
                    <div key={l.id} className="p-3 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 bg-white" data-testid="free-link-row">
                      <Checkbox
                        checked={selLinks.includes(l.id)}
                        onCheckedChange={() => toggleSel(selLinks, setSelLinks, l.id)}
                        className="mt-1 shrink-0"
                        data-testid={`select-link-${l.number}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-[#C86D51]" data-testid={`free-link-number-${l.number}`}>#{l.number}</span>
                          <span className="font-medium text-[#2C221E] text-sm">{l.label || t("owner.freeLinks.noLabel")}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.c}`} data-testid="free-link-status-badge">{badge.t}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${l.sent ? "bg-[#E6EEF6] text-[#3a5a7a]" : "bg-[#F8E3DE] text-[#B35A3E]"}`} data-testid={`free-link-sent-badge-${l.number}`}>
                            {l.sent ? t("owner.freeLinks.sentAt", { date: fmtDate(l.sent_at) }) : t("owner.freeLinks.notSent")}
                          </span>
                        </div>
                        <div className="text-xs text-[#7A6B63] mt-0.5 truncate">
                          {t("owner.freeLinks.createdExpires", { created: fmtDate(l.created_at), expires: fmtDate(l.expires_at) })}
                          {l.collection && (
                            <> · <a href={`/admin/${l.collection.slug}/${l.collection.admin_token}`} target="_blank" rel="noreferrer" className="text-[#B35A3E] hover:underline">{l.collection.person_name}</a></>
                          )}
                        </div>
                        <div className="text-xs text-[#B7A99E] mt-0.5 truncate font-mono">{freeUrl(l.token)}</div>
                        <Input
                          defaultValue={l.note}
                          onBlur={(e) => { if (e.target.value !== l.note) setLinkNote(l, e.target.value); }}
                          placeholder={t("owner.freeLinks.notePlaceholder")}
                          className="h-8 mt-2 rounded-lg border-[#E8DEC2] bg-[#FDFBF7] text-xs"
                          data-testid={`free-link-note-${l.number}`}
                        />
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <label className="flex items-center gap-1.5 text-xs text-[#7A6B63] cursor-pointer">
                          <Checkbox checked={l.sent} onCheckedChange={(v) => setLinkSent(l, v === true)} data-testid={`link-sent-toggle-${l.number}`} />
                          {t("owner.freeLinks.markSent") || "Lähetetty"}
                        </label>
                        <Button
                          onClick={() => copyLink(l.token)} variant="outline"
                          className="h-9 rounded-xl border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] text-sm"
                          data-testid="copy-free-link-button"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" className="h-9 px-2 rounded-xl text-[#B84A39] hover:bg-[#FBECE7]" data-testid={`delete-link-${l.number}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("owner.freeLinks.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {l.status === "used" ? t("owner.freeLinks.deleteUsedWarn") : t("owner.freeLinks.deleteDesc")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteLinks([l.id])} className="bg-[#B84A39] hover:bg-[#9d3e30]" data-testid={`confirm-delete-link-${l.number}`}>
                                {t("owner.freeLinks.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Collections */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-serif text-2xl text-[#2C221E] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#C86D51]" /> {t("owner.collections.title", { n: collections.filter((c) => !c.is_test).length })}
            </h2>
            {selColls.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="h-9 rounded-xl border-[#B84A39] text-[#B84A39] hover:bg-[#FBECE7] text-sm" data-testid="delete-selected-collections-button">
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> {t("owner.collections.deleteSelected")} ({selColls.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("owner.collections.deleteTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("owner.collections.deleteManyDesc", { c: selColls.length })}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteCollections(selColls)} className="bg-[#B84A39] hover:bg-[#9d3e30]" data-testid="confirm-delete-selected-collections">
                      {t("owner.collections.delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          {collections.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8DEC2] bg-white p-6 text-center text-[#7A6B63]">
              {t("owner.collections.empty")}
            </div>
          ) : (
            <div className="space-y-3" data-testid="owner-collections-list">
              {collections.map((c) => (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border p-5 shadow-sm ${c.is_test ? "border-[#D8CBB0] bg-[#FBF7EE]" : "border-[#E8DEC2] bg-white"}`}
                  data-testid={`owner-collection-${c.slug}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selColls.includes(c.id)}
                      onCheckedChange={() => toggleSel(selColls, setSelColls, c.id)}
                      className="mt-1.5 shrink-0"
                      data-testid={`select-collection-${c.slug}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif text-xl text-[#2C221E]">{c.person_name}</span>
                        {c.is_test && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#EFE4CC] text-[#8a6d2f]" data-testid={`test-badge-${c.slug}`}>
                            {t("owner.collections.testBadge")}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-[#7A6B63]">
                        {t("owner.collections.organizer", { name: c.giver_name, date: fmtDate(c.created_at) })}
                      </div>
                      <div className="text-sm text-[#7A6B63]">
                        {t("owner.collections.closes", { date: fmtDate(c.closes_at) })}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-sm flex-wrap">
                        <span className="text-[#4A3E39]">{t("owner.collections.answers", { n: c.answer_count })}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_open ? "bg-[#EAF1EA] text-[#3f6047]" : "bg-[#F3EDE6] text-[#7A6B63]"}`}>
                          {c.is_open ? t("owner.collections.open") : t("owner.collections.closed")}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.marketing_consent ? "bg-[#EAF1EA] text-[#3f6047]" : "bg-[#F3EDE6] text-[#7A6B63]"}`}
                          data-testid={`owner-consent-${c.slug}`}
                        >
                          {c.marketing_consent ? t("owner.collections.consentYes") : t("owner.collections.consentNo")}
                        </span>
                      </div>

                      <div className="mt-3 flex items-center gap-3 flex-wrap">
                        <a
                          href={`/admin/${c.slug}/${c.admin_token}`}
                          className="inline-flex items-center justify-center h-9 px-4 rounded-xl border border-[#C86D51] text-[#B35A3E] hover:bg-[#F8EBE6] transition-colors text-sm font-medium"
                          data-testid={`owner-admin-link-${c.slug}`}
                        >
                          {t("owner.collections.admin")} <ExternalLink className="w-4 h-4 ml-2" />
                        </a>
                        <button
                          type="button"
                          onClick={() => loadAnswers(c)}
                          className="inline-flex items-center gap-1 text-sm text-[#7A6B63] hover:text-[#B35A3E]"
                          data-testid={`toggle-answers-${c.slug}`}
                        >
                          {openAnswers[c.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {openAnswers[c.id] ? t("owner.collections.hideAnswers") : t("owner.collections.showAnswers")}
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleNameForms(c)}
                          className="inline-flex items-center gap-1 text-sm text-[#7A6B63] hover:text-[#B35A3E]"
                          data-testid={`toggle-name-forms-${c.slug}`}
                        >
                          {openNameForms[c.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {t("owner.nameForms.toggle")}
                        </button>
                        <label className="flex items-center gap-1.5 text-sm text-[#7A6B63] cursor-pointer">
                          <Switch checked={!!c.is_test} onCheckedChange={(v) => toggleTest(c, v)} data-testid={`test-toggle-${c.slug}`} />
                          {t("owner.collections.testToggle")}
                        </label>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" className="h-9 px-2 rounded-xl text-[#B84A39] hover:bg-[#FBECE7]" data-testid={`delete-collection-${c.slug}`}>
                              <Trash2 className="w-4 h-4 mr-1" /> {t("owner.collections.delete")}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("owner.collections.deleteTitle")}</AlertDialogTitle>
                              <AlertDialogDescription>{t("owner.collections.deleteDesc", { n: c.answer_count })}</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteCollections([c.id])} className="bg-[#B84A39] hover:bg-[#9d3e30]" data-testid={`confirm-delete-collection-${c.slug}`}>
                                {t("owner.collections.delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>

                      {openAnswers[c.id] && (
                        <div className="mt-3 border-t border-[#F0E8DA] pt-3 space-y-2" data-testid={`answers-list-${c.slug}`}>
                          {(answersByColl[c.id] || []).length === 0 ? (
                            <p className="text-sm text-[#7A6B63]">{t("owner.collections.empty")}</p>
                          ) : (
                            (answersByColl[c.id] || []).map((a) => (
                              <div key={a.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`answer-row-${a.id}`}>
                                <span className="text-[#4A3E39] truncate">
                                  {a.respondent_name || t("owner.collections.anon")} · {fmtDate(a.created_at)}{a.language ? ` · ${a.language}` : ""}
                                </span>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" className="h-8 px-2 rounded-lg text-[#B84A39] hover:bg-[#FBECE7] shrink-0" data-testid={`delete-answer-${a.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent className="rounded-2xl">
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t("owner.collections.deleteAnswerTitle")}</AlertDialogTitle>
                                      <AlertDialogDescription>{t("owner.collections.deleteAnswerDesc")}</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteAnswer(c, a.id)} className="bg-[#B84A39] hover:bg-[#9d3e30]" data-testid={`confirm-delete-answer-${a.id}`}>
                                        {t("owner.collections.delete")}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                      {openNameForms[c.id] && (
                        <div className="mt-3 border-t border-[#F0E8DA] pt-3 space-y-3" data-testid={`name-forms-editor-${c.slug}`}>
                          <div>
                            <p className="text-sm font-medium text-[#2C221E]">{t("owner.nameForms.title")}</p>
                            <p className="text-xs text-[#7A6B63] mt-0.5">{t("owner.nameForms.desc")}</p>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs text-[#7A6B63]">{t("owner.nameForms.fiGenitive")}</Label>
                              <Input
                                value={nameFormDrafts[c.id]?.fiGenitive || ""}
                                onChange={(e) => setNameFormDrafts((d) => ({ ...d, [c.id]: { ...d[c.id], fiGenitive: e.target.value } }))}
                                className="mt-1 rounded-xl"
                                data-testid={`name-form-fi-genitive-${c.slug}`}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-[#7A6B63]">{t("owner.nameForms.fiAllative")}</Label>
                              <Input
                                value={nameFormDrafts[c.id]?.fiAllative || ""}
                                onChange={(e) => setNameFormDrafts((d) => ({ ...d, [c.id]: { ...d[c.id], fiAllative: e.target.value } }))}
                                className="mt-1 rounded-xl"
                                data-testid={`name-form-fi-allative-${c.slug}`}
                              />
                            </div>
                            {(nameFormDrafts[c.id]?.hasDe || c.name_forms?.de) && (
                              <div>
                                <Label className="text-xs text-[#7A6B63]">{t("owner.nameForms.deGenitive")}</Label>
                                <Input
                                  value={nameFormDrafts[c.id]?.deGenitive || ""}
                                  onChange={(e) => setNameFormDrafts((d) => ({ ...d, [c.id]: { ...d[c.id], deGenitive: e.target.value } }))}
                                  className="mt-1 rounded-xl"
                                  data-testid={`name-form-de-genitive-${c.slug}`}
                                />
                              </div>
                            )}
                          </div>
                          <Button
                            onClick={() => saveNameForms(c)}
                            className="h-9 px-4 rounded-xl bg-[#C86D51] hover:bg-[#B35A3E] text-white text-sm"
                            data-testid={`save-name-forms-${c.slug}`}
                          >
                            {t("owner.nameForms.save")}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Occasions */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-[#2C221E] mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C86D51]" /> {t("owner.occasions.title", { n: occasions.length })}
          </h2>
          {occasions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8DEC2] bg-white p-6 text-center text-[#7A6B63]">
              {t("owner.occasions.empty")}
            </div>
          ) : (
            <div className="space-y-3" data-testid="owner-occasions-list">
              {occasions.map((o) => {
                const dirty = occDirty(o);
                return (
                  <div
                    key={o.id}
                    className="rounded-2xl border border-[#E8DEC2] bg-white p-5 shadow-sm"
                    data-testid={`owner-occasion-${o.id}`}
                  >
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${o.is_public ? "bg-[#EAF1EA] text-[#3f6047]" : "bg-[#F3EDE6] text-[#7A6B63]"}`}>
                        {o.is_public ? t("owner.occasions.public") : t("owner.occasions.private")} · {t("owner.occasions.usage", { n: o.usage_count })}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-sm text-[#7A6B63]">{t("owner.occasions.publicToggle")}</span>
                        <Switch
                          checked={o.is_public}
                          onCheckedChange={(v) => togglePublic(o, v)}
                          data-testid={`occasion-public-toggle-${o.id}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Input
                        value={draftVal(o, "title")}
                        onChange={(e) => setDraft(o.id, "title", e.target.value)}
                        placeholder={t("owner.occasions.titlePlaceholder")}
                        className="h-11 rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                        data-testid={`occasion-title-edit-${o.id}`}
                      />
                      <Input
                        value={draftVal(o, "description")}
                        onChange={(e) => setDraft(o.id, "description", e.target.value)}
                        placeholder={t("owner.occasions.descPlaceholder")}
                        className="h-11 rounded-xl border-[#E8DEC2] bg-[#FDFBF7]"
                        data-testid={`occasion-description-edit-${o.id}`}
                      />
                    </div>

                    {renderOccQuestions(o, "fi")}
                    {renderOccQuestions(o, "de")}

                    {dirty && (
                      <Button
                        onClick={() => saveOccasion(o)}
                        className="mt-4 h-10 px-4 bg-[#C86D51] hover:bg-[#B35A3E] text-white rounded-xl"
                        data-testid={`occasion-save-${o.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" /> {t("owner.occasions.saveQuestions")}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Events / analytics */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-[#2C221E] mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C86D51]" /> {t("owner.events.title", { n: events.length })}
          </h2>
          {events.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8DEC2] bg-white p-6 text-center text-[#7A6B63]">
              {t("owner.events.empty")}
            </div>
          ) : (
            <div className="space-y-2" data-testid="owner-events-list">
              {events.map((e) => (
                <div key={e.collection_id} className="rounded-2xl border border-[#E8DEC2] bg-white p-4 shadow-sm text-sm" data-testid={`owner-event-${e.collection_id}`}>
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <span className="font-medium text-[#2C221E]">{e.occasion_title || "—"}</span>
                    <span className="text-xs text-[#7A6B63]">{fmtDate(e.created_at)}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[#4A3E39]">
                    <span>{t("owner.events.answers")} <strong>{e.answer_count}</strong></span>
                    <span>{t("owner.events.regens")} <strong>{e.regen_count}</strong></span>
                    <span>{t("owner.events.rating")} <strong>{e.thumb === "up" ? "👍" : e.thumb === "down" ? "👎" : "–"}</strong></span>
                  </div>
                  {e.feedback_text && (
                    <p className="mt-1 text-[#7A6B63] italic">"{e.feedback_text}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leads */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-[#2C221E] mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#C86D51]" /> {t("owner.leads.title", { n: leads.length })}
          </h2>
          {leads.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8DEC2] bg-white p-6 text-center text-[#7A6B63]">
              {t("owner.leads.empty")}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#E8DEC2] bg-white divide-y divide-[#F0E8DA]" data-testid="owner-leads-list">
              {leads.map((l, i) => (
                <div key={i} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <span className="text-[#4A3E39] text-sm break-all">{l.email}</span>
                  <span className="text-xs text-[#7A6B63]">
                    {l.person_name} · {fmtDate(l.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
